import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type AlertNotification,
  type NotificationSnapshot,
  fetchNotificationSnapshot,
  markAllNotificationsRead,
  markNotificationRead,
} from "../api";

export const NOTIFICATION_POLL_INTERVAL = 60_000;
const isVisible = () => document.visibilityState === "visible" && navigator.onLine;
const stamp = (items: AlertNotification[], id?: string) => {
  const readAt = new Date().toISOString();
  return items.map((item) => (item.read_at || (id && item.id !== id) ? item : { ...item, read_at: readAt }));
};

export function unreadItems(items: AlertNotification[]) {
  return items.filter((item) => !item.read_at);
}
export function newArrivals(knownIds: Set<string>, items: AlertNotification[]) {
  return items.filter((item) => !knownIds.has(item.id));
}

export function useNotificationSnapshot(userId: number | undefined, accountId: number | undefined, enabled: boolean) {
  const client = useQueryClient();
  const key = useMemo(() => ["alert-notifications", userId, accountId], [userId, accountId]);
  const [visible, setVisible] = useState(() => isVisible());
  const [toast, setToast] = useState<AlertNotification[]>([]);
  const knownIds = useRef<Set<string> | null>(null);
  const dismissToast = useCallback((id?: string) => {
    setToast((current) => (id ? current.filter((item) => item.id !== id) : []));
  }, []);

  useEffect(() => {
    knownIds.current = null;
    setToast([]);
  }, [key, enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const synchronize = () => {
      const next = isVisible();
      setVisible(next);
      if (next) {
        void client.invalidateQueries({ queryKey: key, exact: true });
      } else {
        void client.cancelQueries({ queryKey: key, exact: true });
      }
    };
    document.addEventListener("visibilitychange", synchronize);
    window.addEventListener("online", synchronize);
    window.addEventListener("offline", synchronize);
    return () => {
      document.removeEventListener("visibilitychange", synchronize);
      window.removeEventListener("online", synchronize);
      window.removeEventListener("offline", synchronize);
    };
  }, [client, enabled, key]);

  const query = useQuery(
    key,
    async ({ signal }) => {
      const snapshot = await fetchNotificationSnapshot(signal);
      if (signal?.aborted) {
        throw new DOMException("Notification session changed", "AbortError");
      }
      return snapshot;
    },
    {
      enabled: enabled && visible,
      retry: false,
      refetchInterval: enabled && visible ? NOTIFICATION_POLL_INTERVAL : false,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: "always",
      refetchOnReconnect: "always",
    },
  );

  const page = query.data?.items;
  const items = useMemo(() => {
    const source = page ?? [];
    const toastIds = new Set(toast.map((item) => item.id));
    return [
      ...toast.map((item) => source.find((entry) => entry.id === item.id) ?? item),
      ...source.filter((item) => !toastIds.has(item.id)),
    ];
  }, [page, toast]);

  useEffect(() => {
    const snapshot = query.data;
    if (!enabled || !snapshot) {
      return;
    }
    if (knownIds.current) {
      const arrivals = newArrivals(knownIds.current, snapshot.items);
      if (arrivals.length) {
        setToast((current) => {
          const seen = new Set(current.map((item) => item.id));
          const fresh = arrivals.filter((item) => !seen.has(item.id));
          return fresh.length ? [...fresh, ...current] : current;
        });
      }
    }
    knownIds.current = new Set(snapshot.items.map((item) => item.id));
  }, [enabled, query.data]);

  const unread = useMemo(() => unreadItems(items), [items]);
  const updateRead = (id?: string) => {
    client.setQueryData(key, (current: NotificationSnapshot | undefined) =>
      current ? { ...current, items: stamp(current.items, id) } : current,
    );
  };

  return {
    ...query,
    items,
    unread,
    toast,
    markRead: (id: string) => {
      updateRead(id);
      setToast((current) => stamp(current, id));
      void markNotificationRead(id).catch(() => client.invalidateQueries({ queryKey: key, exact: true }));
    },
    markAllRead: () => {
      updateRead();
      setToast((current) => stamp(current));
      void markAllNotificationsRead().catch(() => client.invalidateQueries({ queryKey: key, exact: true }));
    },
    dismissToast,
  };
}
